import { useOrderContext } from "app/providers/OrderProvider";
import {
  lazy,
  useContext,
  useState,
  createContext,
  Dispatch,
  useEffect,
  useMemo,
} from "react";
import { useCartContext } from "app/providers/CartProvider";
import { useCheckoutRequest } from "app/hooks";
import { useSearchParams } from "react-router-dom";
import { useShoppingCart } from "app/hooks/checkout/useShoppingCart";
import { useStandardProductType } from "app/hooks/shared/standard-product/useStandardProductType";
import { STORAGE_ORDER_KEY } from "shared/config";

export enum CheckoutTabIds {
  OVERVIEW = "OVERVIEW",
  PERSONAL_INFO = "PERSONAL_INFO",
  APPOINTMENT = "APPOINTMENT",
  CALENDAR = "CALENDAR",
  CONFIRMATION = "CONFIRMATION",
  NO_PRODUCTS = "NO_PRODUCTS",
  PAYMENT = "PAYMENT",
}

export interface CheckoutTab {
  id: CheckoutTabIds;
  title: string;
  displayOne?: string;
  displaySecond?: string;
  component: React.LazyExoticComponent<() => JSX.Element> | (() => JSX.Element);
  hideNavbar: boolean;
  hidden: boolean;
}

export const CHECKOUT_ORDER_FLOW_TABS: CheckoutTab[] = [
  {
    id: CheckoutTabIds.OVERVIEW,
    title: "Winkelwagen",
    displayOne: "Winkel",
    displaySecond: "wagen",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-overview"),
    ),
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.PERSONAL_INFO,
    title: "Jouw gegevens",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-info"),
    ),
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.APPOINTMENT,
    title: "Afspraak inplannen",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-appointment"),
    ),
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.CONFIRMATION,
    title: "Bevestiging",
    component: () => <></>,
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.NO_PRODUCTS,
    title: "Geen producten",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-empty-cart"),
    ),
    hideNavbar: true,
    hidden: true,
  },
];

export const CHECKOUT_SAMPLE_DOOR_SALE_FLOW_TABS: CheckoutTab[] = [
  {
    id: CheckoutTabIds.OVERVIEW,
    title: "Winkelwagen",
    displayOne: "Winkel",
    displaySecond: "wagen",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/sample-sale-door"),
    ),
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.PERSONAL_INFO,
    title: "Jouw gegevens",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-info"),
    ),
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.APPOINTMENT,
    title: "Afspraak inplannen",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-appointment"),
    ),
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.PAYMENT,
    title: "Betaling",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-payment"),
    ),
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.NO_PRODUCTS,
    title: "Geen producten",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-empty-cart"),
    ),
    hideNavbar: true,
    hidden: true,
  },
];

export const CHECKOUT_SELF_ASSEMBLY_ORDER_FLOW_TABS: CheckoutTab[] = [
  {
    id: CheckoutTabIds.OVERVIEW,
    title: "Winkelwagen",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-overview"),
    ),
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.PERSONAL_INFO,
    title: "Jouw gegevens",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-info"),
    ),
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.APPOINTMENT,
    title: "Afspraak inplannen",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-appointment"),
    ),
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.PAYMENT,
    title: "Betaling",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-payment"),
    ),
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.CONFIRMATION,
    title: "Bevestiging",
    component: () => <></>,
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.NO_PRODUCTS,
    title: "Geen producten",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-empty-cart"),
    ),
    hideNavbar: true,
    hidden: true,
  },
];

/* Afspraak Inplannen is TEMPORARY DISABLED FOR THE LAUNCH */
/* TODO: UNCOMMENT */
export const CHECKOUT_PANEL_FLOW_TABS: CheckoutTab[] = [
  {
    id: CheckoutTabIds.OVERVIEW,
    title: "Winkelwagen",
    displayOne: "Winkel",
    displaySecond: "wagen",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-overview"),
    ),
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.PERSONAL_INFO,
    title: "Jouw gegevens",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-info"),
    ),
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.APPOINTMENT,
    title: "Afspraak inplannen",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-appointment"),
    ),
    hideNavbar: false,
    hidden: false,
  },

  {
    id: CheckoutTabIds.CALENDAR,
    title: "Kalender",
    displayOne: "Kalen",
    displaySecond: "der",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-calendar"),
    ),
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.PAYMENT,
    title: "Betaling",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-payment"),
    ),
    hideNavbar: false,
    hidden: false,
  },

  {
    id: CheckoutTabIds.NO_PRODUCTS,
    title: "Geen producten",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-empty-cart"),
    ),
    hideNavbar: true,
    hidden: true,
  },
];

export const CHECKOUT_ACCESSORIES_FLOW_TABS: CheckoutTab[] = [
  {
    id: CheckoutTabIds.OVERVIEW,
    title: "Winkelwagen",
    displayOne: "Winkel",
    displaySecond: "wagen",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-overview"),
    ),
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.PERSONAL_INFO,
    title: "Jouw gegevens",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-info"),
    ),
    hideNavbar: false,
    hidden: false,
  },

  {
    id: CheckoutTabIds.PAYMENT,
    title: "Betaling",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-payment"),
    ),
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.CONFIRMATION,
    title: "Bevestiging",
    component: () => <></>,
    hideNavbar: false,
    hidden: false,
  },
  {
    id: CheckoutTabIds.NO_PRODUCTS,
    title: "Geen producten",
    component: lazy(
      () => import(/* webpackMode: "eager" */ "../tabs/checkout-empty-cart"),
    ),
    hideNavbar: true,
    hidden: true,
  },
];

interface CheckoutTabsProviderProps {
  currentTab: CheckoutTab;
  checkoutTabs: CheckoutTab[];
  visitedTabs: CheckoutTabIds[];
  disabledTabs: CheckoutTabIds[];

  updateCurrentTab: (id: CheckoutTabIds) => void;
  updateVisitedTabs: Dispatch<React.SetStateAction<CheckoutTabIds[]>>;
  updateDisabledTabs: Dispatch<React.SetStateAction<CheckoutTabIds[]>>;
}

const CheckoutTabsContext = createContext<CheckoutTabsProviderProps>(
  {} as CheckoutTabsProviderProps,
);
export const useCheckoutTabsContext = () => useContext(CheckoutTabsContext);

const CheckoutTabsProvider = ({ children }: any) => {
  const cart = useCartContext().getCart();
  const { isOrderFetching, order } = useOrderContext();
  const [searchParams] = useSearchParams();

  const {
    isCheckoutRequestLoading,
    isCheckoutRequestFailed,
    checkoutRequestData,
  } = useCheckoutRequest();

  const { getFetchedShoppingCart } = useShoppingCart();
  const productAnalysis = useStandardProductType(cart);

  const checkoutTabs = useMemo(() => {
    if (cart.standardProducts.length) {
      if (
        productAnalysis.someAkoestischepanelen ||
        productAnalysis.someAccessoireAkoestischPaneel
      ) {
        return CHECKOUT_PANEL_FLOW_TABS;
      } else {
        return CHECKOUT_ACCESSORIES_FLOW_TABS;
      }
    } else {
      if (cart.sampleSale && cart.sampleSaleRabbetSize) {
        return CHECKOUT_SAMPLE_DOOR_SALE_FLOW_TABS;
      } else if (cart.isSelfAssembly) {
        return CHECKOUT_SELF_ASSEMBLY_ORDER_FLOW_TABS;
      } else {
        return CHECKOUT_ORDER_FLOW_TABS;
      }
    }
  }, [cart.standardProducts.length, productAnalysis.someAkoestischepanelen]);

  const [currentTab, setCurrentTab] = useState(checkoutTabs[0]);
  const [visitedTabs, updateVisitedTabs] = useState<CheckoutTabIds[]>([]);
  const [disabledTabs, updateDisabledTabs] = useState<CheckoutTabIds[]>([]);
  const [orderCheckDone, setOrderCheckDone] = useState(false);

  useEffect(() => {
    if (visitedTabs.includes(currentTab.id)) return;
    updateVisitedTabs([...visitedTabs, currentTab.id]);
  }, [currentTab]);

  useEffect(() => {
    const shoppingCartId = searchParams.get("shoppingCartId");
    if (shoppingCartId) {
      getFetchedShoppingCart(shoppingCartId);
    }
  }, []);

  useEffect(() => {
    const orderReference = searchParams.get("reference") || "";
    const orderId =
      searchParams.get("orderId") ||
      localStorage.getItem(STORAGE_ORDER_KEY) ||
      "";

    if (
      cart.products.length === 0 &&
      cart.supplyProducts.length === 0 &&
      cart.standardProducts?.length === 0 &&
      !orderReference &&
      !orderId &&
      !isOrderFetching &&
      !order
    ) {
      updateCurrentTab(CheckoutTabIds.NO_PRODUCTS);
      // window.scrollTo(0, 0);
    }
  }, [searchParams, isOrderFetching, cart, order]);

  useEffect(() => {
    if (
      checkoutRequestData &&
      !isCheckoutRequestLoading &&
      !isCheckoutRequestFailed
    ) {
      updateVisitedTabs([
        CheckoutTabIds.OVERVIEW,
        CheckoutTabIds.PERSONAL_INFO,
      ]);
      updateDisabledTabs([
        CheckoutTabIds.OVERVIEW,
        CheckoutTabIds.PERSONAL_INFO,
      ]);
      updateCurrentTab(CheckoutTabIds.APPOINTMENT);
    }
  }, [isCheckoutRequestLoading, isCheckoutRequestFailed, checkoutRequestData]);

  useEffect(() => {
    if (order && !orderCheckDone) {
      updateVisitedTabs([
        CheckoutTabIds.OVERVIEW,
        CheckoutTabIds.PERSONAL_INFO,
      ]);
      updateDisabledTabs([
        CheckoutTabIds.OVERVIEW,
        CheckoutTabIds.PERSONAL_INFO,
      ]);
      updateCurrentTab(CheckoutTabIds.APPOINTMENT);
      setOrderCheckDone(true);
    }
  }, [order]);

  const updateCurrentTab = (id: CheckoutTabIds) => {
    const newTab = checkoutTabs.find((tab) => tab.id === id);
    if (newTab) setCurrentTab(newTab);
  };

  return (
    <CheckoutTabsContext.Provider
      value={{
        currentTab,
        updateCurrentTab,
        checkoutTabs,
        visitedTabs,
        updateVisitedTabs,
        disabledTabs,
        updateDisabledTabs,
      }}
    >
      {children}
    </CheckoutTabsContext.Provider>
  );
};
export default CheckoutTabsProvider;
